import React, { useState } from 'react';
import { Button } from '@h2/ui';
import { Label } from '@h2/ui';
import { Input } from '@h2/ui';
import { Switch } from '@h2/ui';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from '@h2/ui';
import { 
  X, 
  Plus, 
  Edit2,
  FileText,
  Search,
  Globe,
  CheckCircle,
  User,
  Video,
  Settings
} from 'lucide-react';
import { useWorkSettingsStore } from '../../../stores/useWorkSettingsStore';
import { useTaskTypeStore } from '../../../stores/useTaskTypeStore';
import { useConfigurationStore } from '../../../stores/useConfigurationStore';

// 아이콘 매핑 객체
const iconMap = {
  FileText,
  Search,
  Globe,
  CheckCircle,
  User,
  Video,
  Settings,
  X,
  Plus,
  Edit2
};

// 아이콘 렌더링 컴포넌트
const TaskTypeIcon: React.FC<{ iconName: string; className?: string }> = ({ iconName, className = "h-4 w-4" }) => {
  const IconComponent = iconMap[iconName as keyof typeof iconMap];
  return IconComponent ? <IconComponent className={className} /> : <Settings className={className} />;
};

interface WorkflowConfigurationStepProps {
  onNext?: () => void;
  onBack?: () => void;
  title?: string;
  description?: string;
  hideTitle?: boolean;
}

type TaskType = 'transcription' | 'translation' | 'transcription_review' | 'translation_review' | 'translation_review2';

interface TaskNode {
  id: string;
  type: TaskType;
  position: { x: number; y: number };
  duration: { value: number; unit: 'hour' | 'day' };
  order: number;
  label: string;
  icon: string;
}

interface TaskTypeItemProps {
  taskType: {
    id: string;
    name: string;
    memo: string;
    languageDependent: boolean;
    icon: string;
    order: number;
  };
  onDragStart: (type: string) => void;
  onClick: (type: string) => void;
}

interface TaskNodeComponentProps {
  node: TaskNode;
  index: number;
  onRemove: (nodeId: string) => void;
  onUpdateDuration: (nodeId: string, duration: { value: number; unit: 'hour' | 'day' }) => void;
  onDragStart: (index: number) => void;
  onDragOver: (index: number) => void;
  onDrop: (index: number) => void;
}

interface DurationPopoverProps {
  value: { value: number; unit: 'hour' | 'day' };
  onChange: (duration: { value: number; unit: 'hour' | 'day' }) => void;
  onClose: () => void;
}

const DurationPopover: React.FC<DurationPopoverProps> = ({ value, onChange, onClose }) => {
  const [isCustom, setIsCustom] = useState(false);
  const [customValue, setCustomValue] = useState(value.value);
  const [customUnit, setCustomUnit] = useState(value.unit);
  
  // 미리 정의된 옵션들
  const presetOptions = [
    { label: '12H', value: 12, unit: 'hour' as const },
    { label: '1D', value: 1, unit: 'day' as const },
    { label: '2D', value: 2, unit: 'day' as const },
  ];
  
  const getCurrentPreset = () => {
    return presetOptions.find(option => 
      option.value === value.value && option.unit === value.unit
    );
  };
  
  const handlePresetSelect = (preset: typeof presetOptions[0]) => {
    onChange({ value: preset.value, unit: preset.unit });
    onClose();
  };
  
  const handleCustomSave = () => {
    onChange({ value: customValue, unit: customUnit });
    onClose();
  };
  
  const currentPreset = getCurrentPreset();
  const showCustomByDefault = !currentPreset;
  
  return (
    <div className="p-4 space-y-4 min-w-[200px]">
      <div className="space-y-2">
        <Label className="text-sm font-medium">빠른 선택</Label>
        <div className="grid grid-cols-3 gap-2">
          {presetOptions.map((preset) => (
            <Button
              key={preset.label}
              variant={currentPreset?.label === preset.label ? "default" : "outline"}
              size="sm"
              onClick={() => handlePresetSelect(preset)}
              className="h-8 text-xs"
            >
              {preset.label}
            </Button>
          ))}
        </div>
      </div>
      
      <div className="border-t pt-3">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm font-medium">커스텀 설정</Label>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsCustom(!isCustom)}
            className="h-6 w-6 p-0"
          >
            <Edit2 className="h-3 w-3" />
          </Button>
        </div>
        
        {(isCustom || showCustomByDefault) && (
          <div className="space-y-3">
            <div className="flex gap-2 items-center">
              <Input
                type="number"
                value={customValue}
                onChange={(e) => setCustomValue(Number(e.target.value))}
                className="w-16"
                min="1"
                max="365"
              />
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant={customUnit === 'hour' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCustomUnit('hour')}
                  className="h-9 px-3 text-xs"
                >
                  시간
                </Button>
                <Button
                  type="button"
                  variant={customUnit === 'day' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCustomUnit('day')}
                  className="h-9 px-3 text-xs"
                >
                  일
                </Button>
              </div>
            </div>
            <Button onClick={handleCustomSave} size="sm" className="w-full">
              적용
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const TaskTypeItem: React.FC<TaskTypeItemProps> = ({ taskType, onDragStart, onClick }) => {
  return (
    <div
      className="p-3 border rounded-lg cursor-pointer hover:bg-accent transition-colors"
      draggable
      onDragStart={() => onDragStart(taskType.id)}
      onClick={() => onClick(taskType.id)}
      title={taskType.memo}
    >
      <div className="flex items-center gap-2">
        <TaskTypeIcon iconName={taskType.icon} className="h-4 w-4" />
        <span className="text-sm font-medium">{taskType.name}</span>
      </div>
    </div>
  );
};

const TaskNodeComponent: React.FC<TaskNodeComponentProps> = ({
  node,
  index,
  onRemove,
  onUpdateDuration,
  onDragStart,
  onDragOver,
  onDrop
}) => {
  const [isEditingDuration, setIsEditingDuration] = useState(false);
  const { getTaskTypeById } = useTaskTypeStore();
  
  const taskTypeInfo = getTaskTypeById(node.type);
  const taskIcon = taskTypeInfo?.icon || 'FileText';
  const taskName = taskTypeInfo?.name || '작업';
  const taskMemo = taskTypeInfo?.memo || '';
  
  return (
    <div
      className="relative bg-white border-2 rounded-lg p-3 min-w-[120px] cursor-move shadow-sm hover:shadow-md transition-shadow"
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver(index);
      }}
      onDrop={() => onDrop(index)}
    >
      <Button
        variant="ghost"
        size="icon"
        className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 text-white hover:bg-red-600"
        onClick={() => onRemove(node.id)}
      >
        <X className="h-4 w-4" />
      </Button>
      
      <div className="text-center">
        <div className="mb-1">
          <TaskTypeIcon iconName={taskIcon} className="h-6 w-6 mx-auto" />
        </div>
        <div className="font-medium text-sm">{taskName}</div>
        
        <Popover open={isEditingDuration} onOpenChange={setIsEditingDuration}>
          <PopoverTrigger asChild>
            <div className="text-xs text-muted-foreground mt-1 cursor-pointer hover:text-primary flex items-center justify-center gap-1">
              ⏱️ {node.duration.value}{node.duration.unit === 'day' ? '일' : '시간'}
              <Edit2 className="h-3 w-3" />
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-auto">
            <DurationPopover
              value={node.duration}
              onChange={(duration) => {
                onUpdateDuration(node.id, duration);
                setIsEditingDuration(false);
              }}
              onClose={() => setIsEditingDuration(false)}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export const WorkflowConfigurationStep: React.FC<WorkflowConfigurationStepProps> = ({
  title = "작업 설정 - 워크플로 구성",
  description = "Task를 추가하고 순서를 정해 워크플로를 구성하세요.",
  hideTitle = false
}) => {
  // Configuration 스토어에서 워크플로 데이터 가져오기
  const { formData, updateField } = useConfigurationStore();
  
  // 워크플로 데이터 (formData에서 직접 가져오기)
  const workflowData = formData.workflowComponent || {
    taskNodes: [],
    connections: []
  };
  
  // WorkSettings 스토어 (UI 상태 관리용)
  const { markStepCompleted } = useWorkSettingsStore();
  
  const {
    filteredTaskTypes,
    searchQuery,
    loadTaskTypes,
    setSearchQuery,
    isLoading: taskTypesLoading
  } = useTaskTypeStore();
  
  const [draggedTaskType, setDraggedTaskType] = useState<string | null>(null);
  const [draggedNodeIndex, setDraggedNodeIndex] = useState<number | null>(null);
  
  // TaskType 데이터 로드
  React.useEffect(() => {
    loadTaskTypes();
  }, [loadTaskTypes]);
  
  const handleTaskTypeDragStart = (type: string) => {
    setDraggedTaskType(type);
  };
  
  // Task 노드 추가
  const handleTaskTypeClick = (type: string) => {
    const newNode = {
      id: Date.now().toString(),
      type,
      order: workflowData.taskNodes.length,
      duration: { value: 1, unit: 'day' as const }
    };
    
    const updatedNodes = [...workflowData.taskNodes, newNode];
    updateField('workflowComponent', {
      ...workflowData,
      taskNodes: updatedNodes
    });
  };
  
  const handleTaskTypeDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedTaskType) {
      handleTaskTypeClick(draggedTaskType);
      setDraggedTaskType(null);
    }
  };
  
  const handleTaskTypeDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };
  
  // 노드 제거
  const removeTaskNode = (nodeId: string) => {
    const updatedNodes = workflowData.taskNodes.filter(node => node.id !== nodeId);
    updateField('workflowComponent', {
      ...workflowData,
      taskNodes: updatedNodes
    });
  };

  // 노드 duration 업데이트
  const updateTaskDuration = (nodeId: string, duration: { value: number; unit: 'hour' | 'day' }) => {
    const updatedNodes = workflowData.taskNodes.map(node =>
      node.id === nodeId ? { ...node, duration } : node
    );
    updateField('workflowComponent', {
      ...workflowData,
      taskNodes: updatedNodes
    });
  };

  // 노드 순서 변경 관련 핸들러
  const handleNodeDragStart = (index: number) => {
    setDraggedNodeIndex(index);
  };
  
  const handleNodeDragOver = (index: number) => {
    // 드래그 오버 시 시각적 피드백을 위해 필요시 구현
  };
  
  const handleNodeDrop = (targetIndex: number) => {
    if (draggedNodeIndex !== null && draggedNodeIndex !== targetIndex) {
      const sortedNodes = [...workflowData.taskNodes].sort((a, b) => a.order - b.order);
      const draggedNode = sortedNodes[draggedNodeIndex];
      
      // 새로운 순서로 노드들을 재배열
      const reorderedNodes = [...sortedNodes];
      reorderedNodes.splice(draggedNodeIndex, 1);
      reorderedNodes.splice(targetIndex, 0, draggedNode);
      
      // order 속성 업데이트
      const updatedNodes = reorderedNodes.map((node, index) => ({
        ...node,
        order: index
      }));
      
      updateField('workflowComponent', {
        ...workflowData,
        taskNodes: updatedNodes
      });
    }
    setDraggedNodeIndex(null);
  };
  
  return (
    <div className="w-full max-w-none h-[700px]">
      {!hideTitle && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold">{title}</h2>
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        </div>
      )}
      <div className={`flex ${hideTitle ? 'h-full' : 'h-[calc(100%-80px)]'}`}>
        {/* Task 타입 패널 */}
        <div className="w-[30%] border-r pr-4 overflow-y-auto">
          <Label className="text-sm font-medium mb-3 block">
            드래그하여 추가:
          </Label>
          
          <Input
            placeholder="Task 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="mb-3"
          />
          
          <div className="space-y-2">
            {taskTypesLoading ? (
              <div className="text-center py-4 text-muted-foreground">
                로딩 중...
              </div>
            ) : (
              filteredTaskTypes.map(taskType => (
                <TaskTypeItem
                  key={taskType.id}
                  taskType={taskType}
                  onDragStart={handleTaskTypeDragStart}
                  onClick={handleTaskTypeClick}
                />
              ))
            )}
          </div>
        </div>
        
        {/* 워크플로 그래프 */}
        <div 
          className="flex-1 pl-4 relative bg-muted/30"
          onDrop={handleTaskTypeDrop}
          onDragOver={handleTaskTypeDragOver}
        >
          {workflowData.taskNodes.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-muted-foreground">
                <div className="text-4xl mb-2">📋</div>
                <p>왼쪽에서 Task를 클릭하거나 드래그하여</p>
                <p>워크플로를 구성하세요</p>
              </div>
            </div>
          ) : (
            <div className="p-4">
            <div className="flex items-center gap-4 flex-wrap">
              {workflowData.taskNodes
                .sort((a, b) => a.order - b.order)
                .map((node, index) => (
                    <React.Fragment key={node.id}>
                      <TaskNodeComponent
                        node={node}
                        index={index}
                        onRemove={removeTaskNode}
                        onUpdateDuration={updateTaskDuration}
                        onDragStart={handleNodeDragStart}
                        onDragOver={handleNodeDragOver}
                        onDrop={handleNodeDrop}
                      />
                      {index < workflowData.taskNodes.length - 1 && (
                        <div className="flex items-center">
                          <div className="w-8 h-0.5 bg-border"></div>
                          <div className="w-0 h-0 border-l-4 border-l-border border-t-2 border-t-transparent border-b-2 border-b-transparent"></div>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};